import React, { useState } from 'react';
import { Card } from '../SubscriptionAppCard';
import { Button } from '../SubscriptionAppButton';
import { Badge } from '../SubscriptionAppBadge';
import type { BadgeProps } from '../SubscriptionAppBadge';
import { Calendar } from '../BulkActionsManager/components/Calendar';
import { ChevronDown, ChevronRight, ShoppingBag, CreditCard, ArrowRight, Wine, Calendar as CalendarIcon } from 'lucide-react';
export interface Order {
  id: string;
  date: string;
  orderId: string;
  chargeId: string;
  amount: string;
  status: 'success' | 'pending' | 'failed';
  isCheckout?: boolean;
  // Release-date packs: label shown instead of the date, e.g. "14 Jul 2027 release"
  releaseLabel?: string;
  // Release-date packs: skip action for the next release
  skip?: {
    label: string;
    confirmMessage: string;
  };
  // Sign-up order paid but held to ship with the next release
  heldForRelease?: boolean;
  // Optional order channel badge, e.g. "Club" or "Online"
  channel?: {
    label: string;
    variant: NonNullable<BadgeProps['variant']>;
  };
  items?: Array<{
    name: string;
    quantity: number;
    price: string;
  }>;
  subtotal?: string;
  taxes?: string;
  shipping?: string;
  shippingType?: string;
  total?: string;
  chargeCreatedAt?: string;
  paymentProcessor?: string;
  shippingAddress?: {
    name: string;
    street: string;
    street2?: string;
    city: string;
    state: string;
    zip: string;
    country: string;
  };
}
export interface OrdersTableProps {
  upcomingOrders?: Order[];
  recentOrders?: Order[];
  onSkipOrder?: (orderId: string) => void;
  // Open the Recent orders section on first render
  defaultRecentExpanded?: boolean;
  // Shown in Recent orders when the list is empty
  recentEmptyMessage?: string;
  'data-id'?: string;
}
export function OrdersTable({
  upcomingOrders = [],
  recentOrders = [],
  onSkipOrder,
  defaultRecentExpanded = false,
  recentEmptyMessage,
  'data-id': dataId
}: OrdersTableProps) {
  const [skipTarget, setSkipTarget] = useState<Order | null>(null);
  const [upcomingExpanded, setUpcomingExpanded] = useState(false);
  const [recentExpanded, setRecentExpanded] = useState(defaultRecentExpanded);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [showDatePicker, setShowDatePicker] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'text-[#10B981]';
      case 'pending':
        return 'text-[#F59E0B]';
      case 'failed':
        return 'text-[#EF4444]';
      default:
        return 'text-[#94A3B8]';
    }
  };
  const getStatusDot = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-[#10B981]';
      case 'pending':
        return 'bg-[#F59E0B]';
      case 'failed':
        return 'bg-[#EF4444]';
      default:
        return 'bg-[#94A3B8]';
    }
  };
  const toggleOrderExpansion = (orderId: string) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };
  const handleReschedule = (orderId: string) => {
    setShowDatePicker(showDatePicker === orderId ? null : orderId);
  };
  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
  };
  const handleSaveDate = (e: React.MouseEvent, orderId: string) => {
    e.stopPropagation();
    // Here you would typically save the date to your backend
    console.log('Saving new date:', selectedDate, 'for order:', orderId);
    setShowDatePicker(null);
  };
  return <Card padding="none" data-id="element-3767" data-mp="22a7nsiegdco3">
      {/* Upcoming Orders Section */}
      <div className="border-b border-[#E2E8F0]" data-id="element-3768" data-mp="1xbwimkz3rqc7">
        <button onClick={() => setUpcomingExpanded(!upcomingExpanded)} className="w-full flex items-center gap-2 p-6 hover:bg-[#F8FAFC] transition-colors" data-id="element-3769" data-mp="1xd7hc6doy7wg">
          {upcomingExpanded ? <ChevronDown className="w-5 h-5 text-[#475569]" data-id="element-3770" data-mp="1ndgpkcgim702" /> : <ChevronRight className="w-5 h-5 text-[#475569]" data-id="element-3771" data-mp="1nftcz551l9ki" />}
          <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-3772" data-mp="3nhatpolurqgc">
            Upcoming orders
          </h3>
        </button>

        {upcomingExpanded && <div className="px-6 pb-6" data-id="element-3773" data-mp="3sgx6xy1ol6p5">
            <div className="space-y-2" data-id="element-3774" data-mp="3dqyb41xlm7cu">
              {upcomingOrders.map(order => <div key={order.id} className="border border-[#E2E8F0] rounded-md overflow-hidden" data-id="element-3775" data-mp="3ip9j074c27sf">
                  {/* Order Row */}
                  <div className="flex items-center justify-between p-4 hover:bg-[#F8FAFC] transition-colors cursor-pointer" onClick={() => toggleOrderExpansion(order.id)} data-id="element-3776" data-mp="34ogmjguh12mo">
                    <div className="flex items-center gap-4" data-id="element-3777" data-mp="2uru5l359mvpx">
                      <button className="text-[#475569] hover:text-[#0EA5E9]" data-id="element-3778" data-mp="2utf22yb54oqf">
                        {expandedOrderId === order.id ? <ChevronDown className="w-4 h-4" data-id="element-3779" data-mp="2zs8c2gr7xxlu" /> : <ChevronRight className="w-4 h-4" data-id="element-3780" data-mp="szpic4ypomlj" />}
                      </button>
                      <span className={`text-sm text-[#0F172A] ${order.releaseLabel ? 'font-medium' : ''}`} data-id="element-3781" data-mp="xzk5bxfd8s1b">
                        {order.releaseLabel ?? order.date}
                      </span>
                      {order.skip && onSkipOrder && <button type="button" onClick={e => {
                  e.stopPropagation();
                  setSkipTarget(order);
                }} className="text-[13px] font-medium text-[#0EA5E9] hover:text-[#0284C7] hover:underline" data-id="element-3782" data-mp="2djb0zl6mcy8j">
                          {order.skip.label}
                        </button>}
                    </div>

                    <div className="flex items-center gap-6" data-id="element-3783" data-mp="2x691gtrzrmxa">
                      <div className="flex items-center gap-2" data-id="element-3784" data-mp="2x5pgt5ayikfk">
                        <ShoppingBag className="w-4 h-4 text-[#10B981]" data-id="element-3785" data-mp="3b4pa4q9ezni2" />
                        <span className="text-sm font-medium text-[#0EA5E9]" data-id="element-3786" data-mp="3665lgi3frxm0">
                          {order.orderId}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-3787" data-mp="1gtk6vn28pd39">
                        <CreditCard className="w-4 h-4 text-[#6F42C1]" data-id="element-3788" data-mp="1bu7dejgv57rp" />
                        <span className="text-sm text-[#475569]" data-id="element-3789" data-mp="3bvnkjh7yn4xs">
                          {order.chargeId}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-[#0F172A] min-w-[100px] text-right" data-id="element-3790" data-mp="3kvk79dashtq7">
                        {order.amount}
                      </span>
                      <div className="flex items-center gap-2 min-w-[80px]" data-id="element-3791" data-mp="3pvyh90vybdqv">
                        <div className={`w-2 h-2 rounded-full ${getStatusDot(order.status)}`} data-id="element-3792" data-mp="3usynbbsswghz"></div>
                        <span className={`text-sm font-medium capitalize ${getStatusColor(order.status)}`} data-id="element-3793" data-mp="3m5ky4m9ubbv">
                          {order.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Order Details */}
                  {expandedOrderId === order.id && order.items && <div className="bg-[#F8FAFC] border-t border-[#E2E8F0]" data-id="element-3794" data-mp="mdop4es17vfh">
                      <div className="p-6 grid grid-cols-2 gap-6" data-id="element-3795" data-mp="mblol0t96jwi">
                        {/* Left Column - Charge Summary */}
                        <div className="space-y-4" data-id="element-3796" data-mp="ri8yn29cd90k">
                          <div data-id="element-3797" data-mp="rhh3fsjnpdat">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-3" data-id="element-3798" data-mp="mjp80ojsz91v">
                              Charge Summary
                            </h4>
                            {order.items.map((item, idx) => <div key={idx} className="flex gap-3 mb-4" data-id="element-3799" data-mp="1f8mhc261dvih" data-mpi={idx}>
                                <div className="w-12 h-12 bg-white border border-[#E2E8F0] rounded flex items-center justify-center flex-shrink-0" data-id="element-3800" data-mp="1a99tbe3fiieb">
                                  <Wine className="w-6 h-6 text-[#6F42C1]" data-id="element-3801" data-mp="15h8lc4dandu3" />
                                </div>
                                <div className="flex-1" data-id="element-3802" data-mp="11gpt497ho5rd">
                                  <div className="font-medium text-[#0F172A] text-sm" data-id="element-3803" data-mp="1xz47byzhid05">
                                    {item.name}
                                  </div>
                                </div>
                                <div className="text-right" data-id="element-3804" data-mp="2cv4w7gcm2vda">
                                  <div className="text-sm text-[#0F172A]" data-id="element-3805" data-mp="2hmwel3lm5usc">
                                    {item.quantity} x {item.price}
                                  </div>
                                </div>
                              </div>)}
                          </div>

                          {/* Totals */}
                          <div className="space-y-2 pt-4 border-t border-[#E2E8F0]" data-id="element-3806" data-mp="2vme97zllc29m">
                            <div className="flex justify-between text-sm" data-id="element-3807" data-mp="30madlzy79vpp">
                              <span className="text-[#475569]" data-id="element-3808" data-mp="30jebs0j8emvz">Subtotal</span>
                              <span className="text-[#475569]" data-id="element-3809" data-mp="2lo84794hmg0j">
                                {order.items.length}{' '}
                                {order.items.length === 1 ? 'item' : 'items'}
                              </span>
                              <span className="text-[#0F172A]" data-id="element-3810" data-mp="1f85zj5lx5nfe">
                                {order.subtotal}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm" data-id="element-3811" data-mp="3oh2l6r4cr9k4">
                              <span className="text-[#475569]" data-id="element-3812" data-mp="3ogu9h2jyuaai">Taxes</span>
                              <span className="text-[#475569]" data-id="element-3813" data-mp="6a64rdstvp5d"></span>
                              <span className="text-[#0F172A]" data-id="element-3814" data-mp="29ch8ljascd0">
                                {order.taxes}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm" data-id="element-3815" data-mp="35qkx4zugt253">
                              <span className="text-[#475569]" data-id="element-3816" data-mp="35ohvtjnv3ml2">Shipping</span>
                              <span className="text-[#475569]" data-id="element-3817" data-mp="3jjk94rv1ge7w">
                                {order.shippingType}
                              </span>
                              <span className="text-[#0F172A]" data-id="element-3818" data-mp="2r1gczd440nuc">
                                {order.shipping}
                              </span>
                            </div>
                            <div className="flex justify-between text-base font-semibold pt-2 border-t border-[#E2E8F0]" data-id="element-3819" data-mp="ipy8z85s3eyd">
                              <span className="text-[#0F172A]" data-id="element-3820" data-mp="dqbxb35lm6ro">Total</span>
                              <span className="text-[#0F172A]" data-id="element-3821" data-mp="9p8gadbte4ov">
                                {order.total}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right Column - Charge Details */}
                        <div className="space-y-4" data-id="element-3822" data-mp="3r5qdjumxbumi">
                          {/* Charge Type */}
                          <Card padding="sm" data-id="element-3823" data-mp="3m6n6y5rapgcf">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-3824" data-mp="3m7gf9b5wkdwo">
                              Charge Type
                            </h4>
                            <div className="text-sm text-[#0F172A]" data-id="element-3825" data-mp="3d4xeqxpladdl">
                              Recurring
                            </div>
                          </Card>

                          {/* Scheduled Date with Reschedule */}
                          <Card padding="sm" data-id="element-3826" data-mp="1nqxu2w8ikwcy">
                            <div className="flex items-center justify-between mb-2" data-id="element-3827" data-mp="1sn51ktgk6fx0">
                              <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider" data-id="element-3828" data-mp="1xnandt05d876">
                                Scheduled Date
                              </h4>
                              <Button variant="ghost" size="sm" iconLeft={<CalendarIcon className="w-3 h-3" data-id="element-3830" data-mp="14zn3yham4nm4" />} onClick={e => {
                        e.stopPropagation();
                        handleReschedule(order.id);
                      }} className="h-auto py-1 px-2 min-w-0 text-xs" data-id="element-3829" data-mp="14ybz0e5fl9tf">
                                Reschedule
                              </Button>
                            </div>
                            <div className="text-sm text-[#0F172A]" data-id="element-3831" data-mp="m8dv8a8o22vl">
                              {order.date}
                            </div>

                            {/* Date Picker */}
                            {showDatePicker === order.id && <div className="mt-3 pt-3 border-t border-[#E2E8F0]" data-id="element-3832" data-mp="1tjbna0p1ws1k">
                                <Calendar value={selectedDate} onChange={handleDateChange} minDate={new Date()} includeTime={true} data-id="element-3833" data-mp="1om1x2itu8cco" />
                                <div className="flex gap-2 mt-2" data-id="element-3834" data-mp="239mxg4zbvxgw">
                                  <Button size="sm" onClick={e => handleSaveDate(e, order.id)} className="flex-1" data-id="element-3835" data-mp="1yatmaj7ikijw">
                                    Save
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={e => {
                          e.stopPropagation();
                          setShowDatePicker(null);
                        }} className="flex-1" data-id="element-3836" data-mp="15n7lfipo7s0q">
                                    Cancel
                                  </Button>
                                </div>
                              </div>}
                          </Card>

                          {/* Charge Created At */}
                          {order.chargeCreatedAt && <Card padding="sm" data-id="element-3837" data-mp="2yj0c0a0o83z2">
                              <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-3838" data-mp="2ylcv4qi60kdp">
                                Charge Created At
                              </h4>
                              <div className="text-sm text-[#0F172A]" data-id="element-3839" data-mp="2on6uec83hkaf">
                                {order.chargeCreatedAt}
                              </div>
                            </Card>}

                          {/* Order Status */}
                          <Card padding="sm" data-id="element-3840" data-mp="3m944pmcbmjba">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-3841" data-mp="3m82vzois28wq">
                              Order Status
                            </h4>
                            <div className="text-sm text-[#F59E0B] capitalize" data-id="element-3842" data-mp="3chgjw4a9s0is">
                              {order.status}
                            </div>
                          </Card>

                          {/* Payment Processor */}
                          <Card padding="sm" data-id="element-3843" data-mp="2har1lzqtgfzi">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-3844" data-mp="2hclnbg9wwc4n">
                              Payment Processor
                            </h4>
                            <div className="text-sm text-[#0F172A]" data-id="element-3845" data-mp="27dmdhudeuag2">
                              {order.paymentProcessor}
                            </div>
                          </Card>

                          {/* Shipping Address */}
                          {order.shippingAddress && <Card padding="sm" data-id="element-3846" data-mp="2v1vtugruu5zt">
                              <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-3847" data-mp="2v377d8b3qs44">
                                Shipping Address
                              </h4>
                              <div className="text-sm text-[#0F172A] space-y-0.5" data-id="element-3848" data-mp="2lcc9t7seo63d">
                                <div data-id="element-3849" data-mp="3isrs9wqtfzg1">{order.shippingAddress.name}</div>
                                <div data-id="element-3850" data-mp="3e0rplwjd2sfr">{order.shippingAddress.street}</div>
                                {order.shippingAddress.street2 && <div data-id="element-3851" data-mp="3mapb9i8mj8vu">{order.shippingAddress.street2}</div>}
                                <div data-id="element-3852" data-mp="3qb95acxwhwdr">
                                  {order.shippingAddress.city},{' '}
                                  {order.shippingAddress.state}{' '}
                                  {order.shippingAddress.zip}
                                </div>
                                <div data-id="element-3853" data-mp="41y58davv86o">{order.shippingAddress.country}</div>
                              </div>
                            </Card>}
                        </div>
                      </div>
                    </div>}
                </div>)}
            </div>
          </div>}
      </div>

      {/* Recent Orders Section */}
      <div data-id="element-3854" data-mp="muiwjqaxp58d">
        <button onClick={() => setRecentExpanded(!recentExpanded)} className="w-full flex items-center gap-2 p-6 hover:bg-[#F8FAFC] transition-colors" data-id="element-3855" data-mp="mvbsdw8cuabq">
          {recentExpanded ? <ChevronDown className="w-5 h-5 text-[#475569]" data-id="element-3856" data-mp="rs2b6rbjnk0u" /> : <ChevronRight className="w-5 h-5 text-[#475569]" data-id="element-3857" data-mp="wtrl23n15rnc" />}
          <h3 className="text-lg font-semibold text-[#0F172A]" data-id="element-3858" data-mp="74ktayc53iq9">
            Recent orders
          </h3>
        </button>

        {recentExpanded && <div className="px-6 pb-6" data-id="element-3859" data-mp="272g2vr3ahcg">
            {recentOrders.length === 0 && recentEmptyMessage && <p className="text-sm text-[#64748B] text-center py-6 border border-dashed border-[#E2E8F0] rounded-md" data-id="element-3860" data-mp="gvp5jqafkphe">
                {recentEmptyMessage}
              </p>}
            <div className="space-y-2" data-id="element-3861" data-mp="pusrr34w9l04">
              {recentOrders.map(order => <div key={order.id} className="border border-[#E2E8F0] rounded-md overflow-hidden" data-id="element-3862" data-mp="px57d9n6jja0">
                  {/* Order Row */}
                  <div className="flex items-center justify-between p-4 hover:bg-[#F8FAFC] transition-colors cursor-pointer" onClick={() => toggleOrderExpansion(order.id)} data-id="element-3863" data-mp="ztrrtxblg8bi">
                    <div className="flex items-center gap-4" data-id="element-3864" data-mp="31kv4st8yhyz8">
                      <button className="text-[#475569] hover:text-[#0EA5E9]" data-id="element-3865" data-mp="31lej7zzee58i">
                        {expandedOrderId === order.id ? <ChevronDown className="w-4 h-4" data-id="element-3866" data-mp="2wnuxyg4a8s72" /> : <ChevronRight className="w-4 h-4" data-id="element-3867" data-mp="1c37n3in89df4" />}
                      </button>
                      <div className="flex items-center gap-3" data-id="element-3868" data-mp="1h2km11rdiay2">
                        <span className="text-sm text-[#0F172A]" data-id="element-3869" data-mp="1h2159odglslm">
                          {order.date}
                        </span>
                        {order.isCheckout && <span className="px-2 py-0.5 bg-[#0F172A] text-white text-xs font-medium rounded" data-id="element-3870" data-mp="17c942bauu186">
                            Checkout
                          </span>}
                        {order.heldForRelease && <span className="px-2 py-0.5 bg-[#E0F2FE] text-[#0369A1] text-xs font-medium rounded" data-id="element-3871" data-mp="xfwcpk2s7zgl">
                            Held for release
                          </span>}
                        {order.channel && <Badge variant={order.channel.variant} data-id="element-3872" data-mp="of80muwy896c">
                            {order.channel.label}
                          </Badge>}
                      </div>
                    </div>

                    <div className="flex items-center gap-6" data-id="element-3873" data-mp="2iqv393m31dxa">
                      <div className="flex items-center gap-2" data-id="element-3874" data-mp="15l37h01cfwge">
                        <ShoppingBag className="w-4 h-4 text-[#10B981]" data-id="element-3875" data-mp="15nfmp5fpvspr" />
                        <span className="text-sm font-medium text-[#0EA5E9]" data-id="element-3876" data-mp="11l13gk84yqxy">
                          {order.orderId}
                        </span>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-3877" data-mp="rupzwysd6jrl">
                        <CreditCard className="w-4 h-4 text-[#6F42C1]" data-id="element-3878" data-mp="mutv4xbueoaz" />
                        <span className="text-sm text-[#475569]" data-id="element-3879" data-mp="mw5030h0y23o">
                          {order.chargeId}
                        </span>
                      </div>
                      <span className="text-sm font-medium text-[#0F172A] min-w-[100px] text-right" data-id="element-3880" data-mp="dv6re4vn56er">
                        {order.amount}
                      </span>
                      <div className="flex items-center gap-2 min-w-[80px]" data-id="element-3881" data-mp="287wlonpx8hmo">
                        <div className={`w-2 h-2 rounded-full ${getStatusDot(order.status)}`} data-id="element-3882" data-mp="285u0qi3mleot"></div>
                        <span className={`text-sm font-medium capitalize ${getStatusColor(order.status)}`} data-id="element-3883" data-mp="2378zqggjuqzb">
                          {order.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Order Details */}
                  {expandedOrderId === order.id && order.items && <div className="bg-[#F8FAFC] border-t border-[#E2E8F0]" data-id="element-3884" data-mp="2pgh6494vjv29">
                      <div className="p-6 grid grid-cols-2 gap-6" data-id="element-3885" data-mp="1ru9x8n1jfgk3">
                        {/* Left Column - Charge Summary */}
                        <div className="space-y-4" data-id="element-3886" data-mp="1wtmm1dbyyxpb">
                          <div data-id="element-3887" data-mp="21s67zdqkc9ho">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-3" data-id="element-3888" data-mp="21qv3tct7gzq1">
                              Charge Summary
                            </h4>
                            {order.items.map((item, idx) => <div key={idx} className="flex gap-3 mb-4" data-id="element-3889" data-mp="3d6jvghhemmx6" data-mpi={idx}>
                                <div className="w-12 h-12 bg-white border border-[#E2E8F0] rounded-md flex items-center justify-center flex-shrink-0" data-id="element-3890" data-mp="3h7n9cyfw68vr">
                                  <Wine className="w-6 h-6 text-[#6F42C1]" data-id="element-3891" data-mp="3i4kndv0xtpbc" />
                                </div>
                                <div className="flex-1" data-id="element-3892" data-mp="3qyz3u0x7f5w1">
                                  <div className="font-medium text-[#0F172A] text-sm" data-id="element-3893" data-mp="3qzh7vjq3yoei">
                                    {item.name}
                                  </div>
                                </div>
                                <div className="text-right" data-id="element-3894" data-mp="2z9m8eu8jdic4">
                                  <div className="text-sm text-[#0F172A]" data-id="element-3895" data-mp="33a621ntwi3ti">
                                    {item.quantity} x {item.price}
                                  </div>
                                </div>
                              </div>)}
                          </div>

                          {/* Totals */}
                          <div className="space-y-2 pt-4 border-t border-[#E2E8F0]" data-id="element-3896" data-mp="nhqopotv2k50">
                            <div className="flex justify-between text-sm" data-id="element-3897" data-mp="sfidi9z7lq4z">
                              <span className="text-[#475569]" data-id="element-3898" data-mp="2czdv43a53j9t">Subtotal</span>
                              <span className="text-[#475569]" data-id="element-3899" data-mp="280aucv16zz7n">
                                {order.items.length}{' '}
                                {order.items.length === 1 ? 'item' : 'items'}
                              </span>
                              <span className="text-[#0F172A]" data-id="element-3900" data-mp="1y9gb7zj8tfqf">
                                {order.subtotal}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm" data-id="element-3901" data-mp="1p8i438dics3m">
                              <span className="text-[#475569]" data-id="element-3902" data-mp="1ka6sas3nk7ir">Taxes</span>
                              <span className="text-[#475569]" data-id="element-3903" data-mp="1kc9u09e63p3b"></span>
                              <span className="text-[#0F172A]" data-id="element-3904" data-mp="1fcwxsy1ep5o2">
                                {order.taxes}
                              </span>
                            </div>
                            <div className="flex justify-between text-sm" data-id="element-3905" data-mp="39qg34u1ohsjx">
                              <span className="text-[#475569]" data-id="element-3906" data-mp="17zpbg8ddmxj6">Shipping</span>
                              <span className="text-[#475569]" data-id="element-3907" data-mp="1cqxk326vueva">
                                {order.shippingType}
                              </span>
                              <span className="text-[#0F172A]" data-id="element-3908" data-mp="y3cg7626obmb">
                                {order.shipping}
                              </span>
                            </div>
                            <div className="flex justify-between text-base font-semibold pt-2 border-t border-[#E2E8F0]" data-id="element-3909" data-mp="1qrqhpvshdq2m">
                              <span className="text-[#0F172A]" data-id="element-3910" data-mp="1vp9wcwt3c4v3">Total</span>
                              <span className="text-[#0F172A]" data-id="element-3911" data-mp="1hoz2rm0ecc68">
                                {order.total}
                              </span>
                            </div>
                          </div>

                          {/* Action Buttons */}
                          <div className="flex gap-2 pt-4" data-id="element-3912" data-mp="acgu8pkesamz">
                            <Button variant="ghost" size="sm" data-id="element-3913" data-mp="ae0aig62elor">
                              Refund in Shopify
                            </Button>
                          </div>
                        </div>

                        {/* Right Column - Charge Details */}
                        <div className="space-y-4" data-id="element-3914" data-mp="crdmrdxpli2s">
                          {/* Charge Type */}
                          <Card padding="sm" data-id="element-3915" data-mp="7rr9j4hvs1tz">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-3916" data-mp="7pxyld08ivg6">
                              Charge Type
                            </h4>
                            <div className="text-sm text-[#0F172A]" data-id="element-3917" data-mp="3bakjz8fd4ajx">
                              {order.isCheckout ? 'Checkout' : 'Recurring'}
                            </div>
                          </Card>

                          {/* Charge ID */}
                          <Card padding="sm" data-id="element-3918" data-mp="2xgtau15jmsoq">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-3919" data-mp="2xfi7g2g0fmy5">
                              Charge ID
                            </h4>
                            <div className="text-sm text-[#0F172A]" data-id="element-3920" data-mp="1vsfty8ci1v8r">
                              {order.chargeId.replace('#', '')}
                            </div>
                          </Card>

                          {/* Charge Created At */}
                          <Card padding="sm" data-id="element-3921" data-mp="29hztl5asacdu">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-3922" data-mp="29juc6cyl1ser">
                              Charge Created At
                            </h4>
                            <div className="text-sm text-[#0F172A]" data-id="element-3923" data-mp="2jf4j9f3u8pze">
                              {order.chargeCreatedAt}
                            </div>
                          </Card>

                          {/* Order Status */}
                          <Card padding="sm" data-id="element-3924" data-mp="2seribu93hjpa">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-3925" data-mp="t52uov2z65k8">
                              Order Status
                            </h4>
                            <div className="text-sm text-[#10B981] capitalize" data-id="element-3926" data-mp="qjv4dmrwa66n">
                              {order.status}
                            </div>
                          </Card>

                          {/* Payment Processor */}
                          <Card padding="sm" data-id="element-3927" data-mp="gu1wowuvib7o">
                            <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-3928" data-mp="bv7arm1m06jc">
                              Payment Processor
                            </h4>
                            <div className="text-sm text-[#0F172A]" data-id="element-3929" data-mp="14iutevjbkn3c">
                              {order.paymentProcessor}
                            </div>
                          </Card>

                          {/* Shipping Address */}
                          {order.shippingAddress && <Card padding="sm" data-id="element-3930" data-mp="vkrb083ms6fr">
                              <h4 className="text-xs font-semibold text-[#0EA5E9] uppercase tracking-wider mb-2" data-id="element-3931" data-mp="3kaqzq8v8z2sl">
                                Shipping Address
                              </h4>
                              <div className="text-sm text-[#0F172A] space-y-0.5" data-id="element-3932" data-mp="3hpszeo57pakp">
                                <div data-id="element-3933" data-mp="3mpp7ozkxzo62">{order.shippingAddress.name}</div>
                                <div data-id="element-3934" data-mp="3mmt7t5pjapez">{order.shippingAddress.street}</div>
                                {order.shippingAddress.street2 && <div data-id="element-3935" data-mp="3cqznv19hjtn6">{order.shippingAddress.street2}</div>}
                                <div data-id="element-3936" data-mp="3cp54hrdv49l7">
                                  {order.shippingAddress.city},{' '}
                                  {order.shippingAddress.state}{' '}
                                  {order.shippingAddress.zip}
                                </div>
                                <div data-id="element-3937" data-mp="2p2q4e5zqvhz7">{order.shippingAddress.country}</div>
                              </div>
                            </Card>}
                        </div>
                      </div>

                      {/* See Details Link */}
                      <div className="px-6 pb-6" data-id="element-3938" data-mp="2k2au5nareqlg">
                        <Button variant="ghost" size="sm" iconRight={<ArrowRight className="w-4 h-4" data-id="element-3940" data-mp="21e0d3mt13ayj" />} data-id="element-3939" data-mp="2k10vavw31edr">
                          View in Shopify
                        </Button>
                      </div>
                    </div>}
                </div>)}
            </div>

            {/* View All Button */}
            <div className="mt-4 flex justify-end" data-id="element-3941" data-mp="2ag9n8bkdx2be"></div>
          </div>}
      </div>

      {/* Skip release confirmation */}
      {skipTarget?.skip && <div className="fixed inset-0 z-[1050] flex items-center justify-center bg-[rgba(15,23,42,0.5)]" onClick={() => setSkipTarget(null)} data-id="element-3942" data-mp="2kcw3en1rn573">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" role="dialog" aria-modal="true" aria-labelledby="skip-release-title" onClick={e => e.stopPropagation()} data-id="element-3943" data-mp="2fd00ys8pvltn">
            <div className="p-6" data-id="element-3944" data-mp="2p3cjyg0vv6xo">
              <h2 id="skip-release-title" className="text-[18px] font-semibold text-[#0F172A]" data-id="element-3945" data-mp="2p4e6phr9hhv8">
                {skipTarget.skip.label}?
              </h2>
              <p className="text-[14px] text-[#475569] mt-2" data-id="element-3946" data-mp="2y52a7ij13ztv">
                {skipTarget.skip.confirmMessage}
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#E2E8F0]" data-id="element-3947" data-mp="9b2myqtxhe7u">
              <Button variant="secondary" onClick={() => setSkipTarget(null)} data-id="element-3948" data-mp="df0kix3lugys">
                Cancel
              </Button>
              <Button onClick={() => {
            onSkipOrder?.(skipTarget.id);
            setSkipTarget(null);
          }} data-id="element-3949" data-mp="nb3irrvc5vgg">
                Skip release
              </Button>
            </div>
          </div>
        </div>}
    </Card>;
}